import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Achievement } from '@ai-platform/utils';

import { User } from '../../../contexts/UserContext';
import { APP_NAME, ProfileNestedRoutes, RoutePaths, SEARCH_PARAMS } from '../../../constants';
import { MetrikaGoal, useMetrika } from '../../../hooks';
import { ShareMethod, shareUrl } from '../../../utils/share';
import { achievementApi, StateSchema } from '../../../store';

export const useCachedAchievement = (achievementId?: string | null) => {
    return useSelector((state: StateSchema) => {
        if (!achievementId) {
            return null;
        }

        // Получаем все закешированные аргументы для getAchievements
        const cachedArgs = achievementApi.util.selectCachedArgsForQuery(state, 'getAchievements');

        // Ищем ачивку в любом кеше
        for (const args of cachedArgs) {
            const result = achievementApi.endpoints.getAchievements.select(args)(state);

            if (result.data) {
                const found = result.data.find((a: Achievement) => a.id === achievementId);

                if (found) {
                    return found;
                }
            }
        }

        return null;
    });
};

export const useShareAchievement = (achievement?: Achievement | null, user?: User) => {
    const [popover, setPopover] = useState({ open: false, text: '' });
    const [disabled, setDisabled] = useState(false);
    const { reachGoal } = useMetrika();

    const closePopover = useCallback(() => {
        setPopover(prev => ({ ...prev, open: false }));
    }, []);

    const shareAchievement = useCallback(async () => {
        if (!achievement || disabled) {
            return;
        }

        if (!user?.isAuth) {
            setPopover(prev => ({ ...prev, open: true, text: 'Войдите в аккаунт' }));

            return;
        }

        if (!user?.isPublicProfile) {
            setPopover(prev => ({ ...prev, open: true, text: 'Сделайте профиль публичным' }));

            return;
        }

        setDisabled(true);

        try {
            const { id, title } = achievement;
            const userSlug = user.publicId;

            const url = new URL(
                [APP_NAME + RoutePaths.UserProfile, userSlug, ProfileNestedRoutes.Achievements].join('/'),
                window.location.origin,
            );
            url.searchParams.set(SEARCH_PARAMS.ACHIEVEMENT, id);

            const result = await shareUrl({
                title: `Брелок "${title}"`,
                url: url.toString(),
            });

            if (result.success) {
                reachGoal(MetrikaGoal.ClickShareAchievement);

                if (result.method === ShareMethod.Clipboard) {
                    setPopover(prev => ({ ...prev, open: true, text: 'Скопировано' }));
                }
            }
        } finally {
            setDisabled(false);
        }
    }, [achievement, disabled, reachGoal, user?.isAuth, user?.isPublicProfile, user?.publicId]);

    useEffect(() => {
        return closePopover;
    }, [closePopover]);

    return { popover, shareAchievement, closePopover };
};
